import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const LANGUAGES = ['pt', 'en', 'es'];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Helper to detect region from URL
const getRegionFromURL = () => {
  const path = window.location.pathname;
  if (path.startsWith('/ie')) return 'ie';
  if (path.startsWith('/pt')) return 'pt';
  return 'pt';
};

// Get default language based on region
const getDefaultLanguageForRegion = (region) => {
  return region === 'ie' ? 'en' : 'pt';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const region = getRegionFromURL();
    // Check if user manually changed language (stored with region prefix)
    const savedKey = `sancur-language-${region}`;
    const saved = localStorage.getItem(savedKey);
    if (saved && LANGUAGES.includes(saved)) {
      return saved;
    }
    // Default based on region
    return getDefaultLanguageForRegion(region);
  });

  useEffect(() => {
    // Save language preference per region
    const region = getRegionFromURL();
    localStorage.setItem(`sancur-language-${region}`, language);
  }, [language]);

  // Update language when region changes (navigation between /pt and /ie)
  useEffect(() => {
    const handleLocationChange = () => {
      const region = getRegionFromURL();
      const savedKey = `sancur-language-${region}`;
      const saved = localStorage.getItem(savedKey);
      if (saved && LANGUAGES.includes(saved)) {
        setLanguage(saved);
      } else {
        setLanguage(getDefaultLanguageForRegion(region));
      }
    };
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  const changeLanguage = (lang) => {
    if (LANGUAGES.includes(lang)) {
      setLanguage(lang);
    }
  };

  // Legacy toggle function for backward compatibility
  const toggleLanguage = () => {
    const currentIndex = LANGUAGES.indexOf(language);
    const nextIndex = (currentIndex + 1) % LANGUAGES.length;
    setLanguage(LANGUAGES[nextIndex]);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
